import { RefreshCw01 } from '@untitledui/icons';
import { useCallback } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { cx } from '@/utils/cx';

import { ShowReasoningButton } from './ShowReasoningButton';

/**
 * Why a retry button is disabled: a retry for this conversation is in flight.
 * Always passed as a string, never toggled to `undefined` — Button's tooltip
 * wrapper changes shape between string and non-string titles, which would
 * remount the button mid-press. Button hides it while enabled.
 */
export const RETRYING_TOOLTIP = 'Retrying…';

interface ChainRetryProps {
  chainId: string;
  /** Retry is offered only on the most recent chain. */
  canRetry: boolean;
  isRetryDisabled: boolean;
  isRegenerating: boolean;
  onRetry: (chainId: string) => void;
}

interface ChainMaxStepsNoticeProps extends ChainRetryProps {
  onOpenReasoningSidebar: (chainId: string) => void;
}

/** A completed chain that ran out of steps before producing a final answer. */
export function ChainMaxStepsNotice({
  chainId,
  canRetry,
  isRetryDisabled,
  isRegenerating,
  onRetry,
  onOpenReasoningSidebar,
}: ChainMaxStepsNoticeProps) {
  const handleRetry = useCallback(() => onRetry(chainId), [onRetry, chainId]);

  return (
    <div className='space-y-2'>
      <div className='bg-utility-warning-50 border border-utility-warning-200 rounded-xl px-4 py-3'>
        <div className='flex items-center gap-3'>
          <svg
            className='w-5 h-5 text-utility-warning-600 flex-shrink-0'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
            />
          </svg>
          <span className='text-sm text-utility-warning-700 font-medium'>
            This request was too complex to complete. Try again, or break it
            into smaller parts.
          </span>
        </div>
      </div>

      <div className='flex items-center justify-between pt-1'>
        <ShowReasoningButton
          chainId={chainId}
          onOpenReasoningSidebar={onOpenReasoningSidebar}
        />
        {canRetry && (
          <Button
            color='primary'
            onClick={handleRetry}
            isDisabled={isRetryDisabled}
            disabledTooltip={RETRYING_TOOLTIP}
            aria-label='Try again'
            // Warning-colored overrides only while enabled — when disabled they
            // would strip Button's disabled shadow/ring and keep it looking
            // actionable despite `aria-disabled`. Let Button's disabled styling
            // show through instead.
            className={cx(
              !isRetryDisabled &&
                '!bg-utility-warning-600 hover:!bg-utility-warning-700 !shadow-none !ring-0 before:!border-transparent'
            )}
            iconLeading={<RetryIcon isSpinning={isRegenerating} />}
          >
            Try again
          </Button>
        )}
      </div>
    </div>
  );
}

/** A chain whose run failed outright. */
export function ChainFailedNotice({
  chainId,
  canRetry,
  isRetryDisabled,
  isRegenerating,
  onRetry,
}: ChainRetryProps) {
  const handleRetry = useCallback(() => onRetry(chainId), [onRetry, chainId]);

  return (
    <div className='bg-utility-error-50 border border-utility-error-200 rounded-xl px-4 py-3'>
      <div className='flex items-center justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <svg
            className='w-5 h-5 text-utility-error-600 flex-shrink-0'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
          <span className='text-sm text-utility-error-700 font-medium'>
            We couldn't generate a response. Try again.
          </span>
        </div>
        {canRetry && (
          <Button
            color='primary-destructive'
            onClick={handleRetry}
            isDisabled={isRetryDisabled}
            disabledTooltip={RETRYING_TOOLTIP}
            aria-label='Retry'
            iconLeading={<RetryIcon isSpinning={isRegenerating} />}
          >
            Retry
          </Button>
        )}
      </div>
    </div>
  );
}

function RetryIcon({ isSpinning }: { isSpinning: boolean }) {
  return (
    <RefreshCw01
      className={cx('size-4', isSpinning && 'animate-spin')}
      strokeWidth={2}
    />
  );
}
